// apps/web/src/pages/author/widget-config/option-set-config.tsx
//
// Phase 8 config sub-form for template `option-set` — drives the inline
// configSchema editor per authr-05. Field set mirrors
// packages/shared/src/schemas/widget-templates/option-set.ts AND the per-template
// walker at packages/shared/src/forms/per-template/option-set.ts (Plan 03).
//
// Sub-form fields use per-path useWatch only — NEVER top-level (RESEARCH
// §"Pattern 3"). No formState reads. Every useFieldArray map uses field.id
// keys per authr-03 (Plan 01 grep gate enforces).
//
// configSchema shape (verbatim):
//   options: ReadonlyArray<{ id; label; tierId?; categoryIds? }>
//   cardinality: { min; max? }
//
// W11 — exports `defaultOptionSetConfig(): OptionSetConfig` factory used by
// Task 3's Add-widget flow to seed defaults. Single source of truth: do NOT
// duplicate per-template defaults in WidgetInstances.tsx.

import type { OptionSetConfig } from "@the-recipe-spread/shared";
import { useFieldArray, useFormContext } from "react-hook-form";
import { EmptyState } from "@/components/ui/empty-state";
import { Fieldset } from "@/components/ui/fieldset";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FieldArrayRow } from "../FieldArrayRow";

export interface OptionSetConfigSubFormProps {
  instanceId: string;
  basePath: string;
}

// W11 — minimum-valid OptionSetConfig per the configSchema:
//   options: at least one entry recommended; the schema permits `.min(0)` but
//   a fresh widget with zero options has no useful UI — seed with one blank.
//   cardinality.min: 1 — single-select default.
export function defaultOptionSetConfig(): OptionSetConfig {
  return {
    options: [{ id: "option-1", label: "Option 1" }],
    cardinality: { min: 1 },
  };
}

export function OptionSetConfigSubForm({
  instanceId: _instanceId,
  basePath,
}: OptionSetConfigSubFormProps): React.ReactElement {
  const { control } = useFormContext();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: `${basePath}.options`,
  });

  return (
    <div className="flex flex-col gap-4">
      <Fieldset legend="OPTIONS">
        {fields.length === 0 ? (
          <EmptyState
            title="No options yet."
            cta={{
              label: "Add option",
              onClick: () =>
                append({
                  id: `option-${fields.length + 1}`,
                  label: `Option ${fields.length + 1}`,
                }),
            }}
          />
        ) : (
          <>
            {fields.map((field, index) => (
              <FieldArrayRow
                key={field.id}
                singular="option"
                index={index}
                canMoveUp={index > 0}
                canMoveDown={index < fields.length - 1}
                onMoveUp={() => move(index, index - 1)}
                onMoveDown={() => move(index, index + 1)}
                onRemove={() => remove(index)}
                deleteDialogTitle="Delete option?"
                deleteDialogBody="This option will be removed from the option set."
                deleteConfirmLabel="Delete option"
              >
                <FormField
                  control={control}
                  name={`${basePath}.options.${index}.id`}
                  render={({ field: f }) => (
                    <FormItem data-field-path={`${basePath}.options.${index}.id`}>
                      <FormLabel>Id (kebab-case)</FormLabel>
                      <FormControl>
                        <Input {...f} value={f.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`${basePath}.options.${index}.label`}
                  render={({ field: f }) => (
                    <FormItem data-field-path={`${basePath}.options.${index}.label`}>
                      <FormLabel>Label</FormLabel>
                      <FormControl>
                        <Input {...f} value={f.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`${basePath}.options.${index}.tierId`}
                  render={({ field: f }) => (
                    <FormItem data-field-path={`${basePath}.options.${index}.tierId`}>
                      <FormLabel>Tier id (optional)</FormLabel>
                      <FormControl>
                        <Input {...f} value={f.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`${basePath}.options.${index}.categoryIds`}
                  render={({ field: f }) => (
                    <FormItem data-field-path={`${basePath}.options.${index}.categoryIds`}>
                      <FormLabel>Category ids (comma-separated, optional)</FormLabel>
                      <FormControl>
                        <Textarea
                          value={Array.isArray(f.value) ? (f.value as string[]).join(", ") : ""}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const parts = raw
                              .split(",")
                              .map((s) => s.trim())
                              .filter((s) => s.length > 0);
                            f.onChange(parts.length === 0 ? undefined : parts);
                          }}
                          onBlur={f.onBlur}
                          name={f.name}
                          ref={f.ref}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FieldArrayRow>
            ))}
            <button
              type="button"
              onClick={() =>
                append({
                  id: `option-${fields.length + 1}`,
                  label: `Option ${fields.length + 1}`,
                })
              }
              className="self-start text-sm text-muted-foreground hover:text-foreground underline underline-offset-2"
            >
              Add option
            </button>
          </>
        )}
      </Fieldset>

      <Fieldset legend="CARDINALITY">
        <FormField
          control={control}
          name={`${basePath}.cardinality.min`}
          render={({ field: f }) => (
            <FormItem data-field-path={`${basePath}.cardinality.min`}>
              <FormLabel>Min</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={f.value ?? ""}
                  onChange={(e) => {
                    const raw = e.target.value;
                    f.onChange(raw === "" ? undefined : Number(raw));
                  }}
                  onBlur={f.onBlur}
                  name={f.name}
                  ref={f.ref}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={`${basePath}.cardinality.max`}
          render={({ field: f }) => (
            <FormItem data-field-path={`${basePath}.cardinality.max`}>
              <FormLabel>Max (optional)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={f.value ?? ""}
                  onChange={(e) => {
                    const raw = e.target.value;
                    f.onChange(raw === "" ? undefined : Number(raw));
                  }}
                  onBlur={f.onBlur}
                  name={f.name}
                  ref={f.ref}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </Fieldset>
    </div>
  );
}
OptionSetConfigSubForm.displayName = "OptionSetConfigSubForm";
